import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { useFetcher } from "react-router";
import Input from "@components/forms/Input";
import { AUTH_ROUTES } from "@constants/routes";
import { useSubmitEmailMfaCode } from "@hooks/useSubmitEmailMfaCode";

export interface EmailCodeMfaCredentials {
  email: string;
  twoFactorCodeToken: string;
}

interface Props {
  credentials: EmailCodeMfaCredentials | null;
  redirectTo: string | null;
}

export default function EmailMfaCodeForm({ credentials, redirectTo }: Props) {
  const fetcher = useFetcher();
  const {
    onSubmitEmailMfaCode,
    loadingEmailMfaCode,
    emailMfaCodeError,
    setEmailMfaCodeError,
  } = useSubmitEmailMfaCode();
  const [code, setCode] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const posthog = usePostHog();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (fetcher.state === "idle") {
      setResendingCode(false);
    }
  }, [fetcher.state]);

  useEffect(() => {
    if (!credentials) return;

    const submitCode = async () => {
      posthog.capture("email_mfa_code_submitted");
      setHasSubmitted(true);
      await onSubmitEmailMfaCode({
        code,
        token: credentials.twoFactorCodeToken,
        email: credentials.email,
        postHogDistinctId: posthog.get_distinct_id(),
        redirectTo,
      });
    };

    if (code.length === 6 && !hasSubmitted) {
      submitCode();
    }
  }, [
    code,
    credentials,
    hasSubmitted,
    posthog,
    onSubmitEmailMfaCode,
    redirectTo,
  ]);

  if (!credentials) return null;

  const { email } = credentials;

  const handleResendCodeOnKeyDown = async (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      await handleResendCode();
    }
  };
  const handleResendCode = async () => {
    setResendingCode(true);
    const data = new FormData();
    data.append("email", email);
    await fetcher.submit(data, {
      action: AUTH_ROUTES.LOGIN,
      method: "post",
    });
  };

  const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, ""));
    setHasSubmitted(false);
    setEmailMfaCodeError(null);
  };

  return (
    <div className="flex flex-col gap-4 mt-4">
      <Input
        id="verificationCode"
        dataTestId="verificationCode"
        placeholder="Enter 6-digit code"
        inputMode="numeric"
        maxLength={6}
        label={{
          text: "Verification code",
        }}
        value={code}
        onChange={handleCodeChange}
        disabled={loadingEmailMfaCode}
        labelFontBold={false}
        inputRef={inputRef}
        ariaDescribedBy="code-description"
        loading={loadingEmailMfaCode}
        autoComplete="one-time-code"
        pattern="[0-9]*"
      />
      <span
        role="button"
        tabIndex={0}
        onClick={handleResendCode}
        onKeyDown={handleResendCodeOnKeyDown}
        className="underline cursor-pointer bodySmall text-darkGrey text-center"
      >
        {resendingCode ? "Resending..." : "Resend code"}
      </span>
      {emailMfaCodeError && (
        <p className="bodySmall text-center text-red-700 mt-2">
          {emailMfaCodeError}
        </p>
      )}
    </div>
  );
}
